import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { TradeItem } from '@/types';

/**
 * Bridges the MTM page's row data to the MTM filter sidebar (LR-469). The sidebar
 * is a Layout sibling of MtmPage (not a child), so it cannot receive rows as props
 * — it reads them here instead. MtmPage publishes two views on every change:
 *  - `mtmRows`: the active counterparty subtab's book BEFORE sidebar filters — the
 *    stable source for the security-filter option list (so it never shrinks when a
 *    symbol is picked);
 *  - `mtmExportRows`: what the CSV export emits — the active bucket's MARKING scope
 *    (sidebar-filtered, markable lend/borrow rows), pre-exclusion. The same row set
 *    the on-page totals footer reads, so footer and export reconcile (kvi #1).
 *
 * LR-538 adds a second publisher: MtmTable pushes the grid's transient EDIT state
 * (`invalidIds` + the repo call scope) here for the same reason — the export needs to
 * reproduce the synthetic column totals exactly as the footer computed them, and both
 * inputs live inside the table (invalidIds is local `useState`; the call scope arrives as
 * props the sidebar never sees). Without them a red-bordered row would contribute its
 * default-price delta to the file while showing "—" on screen.
 *
 * The call scope is stored as two primitives rather than a `RepoCallScope` object so the
 * publishing effect can key on them directly (a fresh object every render would write to
 * the store on every render); consumers compose the object at the point of use.
 *
 * Not persisted: this is transient per-render data, not user preference.
 */
interface MtmDataStore {
  mtmRows: TradeItem[];
  mtmExportRows: TradeItem[];
  /** Rows whose New Price input is currently invalid — their delta cells render "—". */
  invalidIds: Set<number>;
  /** Whether the counterparty has no GMRA agreement, in which case NOTHING is callable — see
   *  `isRepoCallableInScope`.
   *
   *  PRISTINE VALUE IS `true`, and that is the point (kvi r3 #3): with no grid mounted there is
   *  no published scope, and a `false` default made the export's repo gate fall OPEN instead of
   *  closed. A mounted grid always publishes its real value. */
  noGmraAgreement: boolean;
  setMtmData: (rows: TradeItem[], exportRows: TradeItem[]) => void;
  setMtmEditState: (invalidIds: Set<number>, noGmraAgreement: boolean) => void;
  /** Back to the pristine slice on grid unmount — a stale invalid marker must not
   *  outlive the table and silently drop rows from the next export's totals. */
  resetMtmEditState: () => void;
}

const EMPTY_INVALID_IDS: Set<number> = new Set();

export const useMtmDataStore = create<MtmDataStore>()(
  devtools(
    (set) => ({
      mtmRows: [],
      mtmExportRows: [],
      invalidIds: EMPTY_INVALID_IDS,
      noGmraAgreement: true,
      setMtmData: (mtmRows, mtmExportRows) => set({ mtmRows, mtmExportRows }),
      setMtmEditState: (invalidIds, noGmraAgreement) =>
        set({ invalidIds, noGmraAgreement }),
      resetMtmEditState: () =>
        set({
          invalidIds: EMPTY_INVALID_IDS,
          noGmraAgreement: true,
        }),
    }),
    { name: 'mtm-data-store', enabled: import.meta.env.DEV },
  ),
);
